/** Types for the JSON-driven flow configuration engine */

/** Supported field input types */
export type FlowFieldType =
  | 'text'
  | 'email'
  | 'tel'
  | 'number'
  | 'date'
  | 'date-picker-airbnb'
  | 'select'
  | 'checkbox'
  | 'textarea'
  | 'radio'
  | 'file'
  | 'hidden'
  | 'city-grid'

/** Condition operators for visibility / validation rules */
export type ConditionOperator = 'eq' | 'neq' | 'in' | 'notIn'

/** A condition that references another field's value */
export interface FlowCondition {
  /** Key of the field to evaluate */
  field: string
  /** Comparison operator */
  operator: ConditionOperator
  /** Expected value(s) */
  value: string | string[] | boolean | number
}

/** Validation constraints for a field */
export interface FlowFieldValidation {
  min?: number
  max?: number
  minLength?: number
  maxLength?: number
  pattern?: string
  patternMessage?: string
  /** For checkboxes that must be checked (e.g. privacy consent) */
  mustBeTrue?: boolean
  /** For date fields: user must be at least N years old */
  minAge?: number
  /** 0-indexed months to disable in date picker (0 = Jan, 6 = Jul, 7 = Aug …) */
  disabledMonths?: number[]
}

/** Static option for select / radio fields */
export interface FlowFieldOption {
  value: string
  label: string
  /** Backward-compatible aliases used by some legacy payloads */
  slug?: string
  name?: string
  /** Extra mapped values to save in other form fields */
  meta?: Record<string, unknown>
}

/**
 * Configuration for fetching options from a remote endpoint.
 * The engine will GET the endpoint and map the response array
 * using valueKey / labelKey to build the options list.
 */
export interface FlowRemoteOptions {
  /** Internal API endpoint, e.g. "/api/booking-student/lookups/cities" */
  endpoint: string
  /** Key in each response item to use as option value */
  valueKey: string
  /** Key in each response item to use as option label */
  labelKey: string
  /**
   * If this select depends on another field, specify the parent
   * field key here. The parent's current value will be sent as
   * a query param: ?{dependsOn}={parentValue}
   */
  dependsOn?: string
  /**
   * Base path for images (used by city-grid).
   * Each option's image is resolved as `${imageBasePath}/${slug}.jpg`.
   */
  imageBasePath?: string
  /**
   * Optional mapping from target form field key -> source response key.
   * Example: { bando: 'bando' } stores the selected option's `bando` into form field `bando`.
   */
  extraFields?: Record<string, string>
  /**
   * Static options prepended at the beginning of the list (after sort).
   * These always appear first regardless of sorting.
   */
  prependOptions?: FlowFieldOption[]
  /**
   * Static options appended at the end of the list (after sort).
   * These always appear last regardless of sorting.
   */
  appendOptions?: FlowFieldOption[]
}

/** A single field inside a step */
export interface FlowFieldConfig {
  /** Unique field key (used as form field name and Zod key) */
  key: string
  /** Display label */
  label: string
  /** Input type */
  type: FlowFieldType
  /** Whether the field is required (default false) */
  required?: boolean
  /** Placeholder text */
  placeholder?: string
  /** Default value */
  defaultValue?: string | number | boolean | string[]
  /** Static options for select / radio */
  options?: FlowFieldOption[]
  /** Sort select/radio options by label (default: true) */
  sortOptions?: boolean
  /** Remote options configuration */
  remoteOptions?: FlowRemoteOptions
  /** Validation rules */
  validation?: FlowFieldValidation
  /** Show this field only when the condition is met */
  visibleWhen?: FlowCondition
  /** Show this field only when all conditions are met (AND) */
  visibleWhenAll?: FlowCondition[]
  /** Show this field when any condition group is met (OR of AND-groups) */
  visibleWhenAny?: FlowCondition[][]
  /** Rich text for checkbox labels (supports HTML in label) */
  checkboxText?: string
  /** Render radio options in a row instead of a column */
  row?: boolean
  /** Grid column span (1-12). Default 12 = full width */
  colSpan?: number
  /** Helper text shown below the field */
  helperText?: string
}

/** Step layout variant */
export type FlowStepLayout = 'form' | 'centered' | 'summary'

/** A single step in the wizard flow */
export interface FlowStepConfig {
  /** Step number (1-based, unique across the flow) */
  id: number
  /** Backend save step number for /admissions/save payload */
  saveStep?: number
  /** If true, submit a partial save when user clicks Next on this step */
  saveOnNext?: boolean
  /** URL slug for routing */
  slug: string
  /**
   * Main step group number (1-based).
   * Sub-steps share the same group, e.g. step 1.1 and 1.2 both have group: 1.
   * The progress bar advances per group, not per id.
   */
  group: number
  /** Full title (shown in the step content) */
  title: string
  /**
   * Optional title template with `{{fieldKey}}` placeholders resolved
   * from previously saved form data. E.g. "CIAO {{nome}}, ..."
   */
  titleTemplate?: string
  /** Short title (shown in progress indicators) */
  shortTitle?: string
  /** Title shown in the summary/recap sections for this step's group */
  summaryTitle?: string
  /** Description paragraph below the title */
  description?: string
  /** Layout variant. 'centered' for intro steps, 'summary' for review */
  layout?: FlowStepLayout
  /** Fields in this step */
  fields: FlowFieldConfig[]
  /** Mark as the final/summary step */
  isFinal?: boolean
  /** Show a side panel with previously entered data recap */
  showSideSummary?: boolean
  /** Use full 12-column width instead of centered 8 columns */
  fullWidth?: boolean
}

/**
 * Rule to redirect to a dynamic success URL based on form data.
 * Each rule matches against conditions, and if matched, specifies the redirect URL.
 * URLs can be specified per-language or as a single default URL.
 */
export interface SuccessRedirectRule {
  /** Human-readable name for this rule (for debugging) */
  name: string
  /**
   * External URL(s) to redirect to.
   * Can be a single URL string, or an object mapping language codes to URLs.
   * Example: "https://example.com/thank-you" or { it: "https://example.com/it/grazie", en: "https://example.com/en/thank-you" }
   * If a language-specific URL is not found, the first value in the object is used as fallback.
   */
  redirectUrl: string | Record<string, string>
  /** Exact conditions that must match (AND) */
  conditions?: FlowCondition[]
  /** All condition groups that must match (AND) */
  conditionsAll?: FlowCondition[]
  /** Any condition group that must match (OR of AND-groups) */
  conditionsAny?: FlowCondition[][]
}

/** A complete flow configuration (one per journey) */
export interface FlowConfig {
  /** Unique flow identifier, e.g. "booking-student" */
  id: string
  /** Base URL path, e.g. "/booking-student" */
  path: string
  /** Display title */
  title: string
  /** Active variant key (determines which field set is active) */
  variant?: string
  /** Ordered list of steps */
  steps: FlowStepConfig[]
  /**
   * Optional list of rules to redirect to external success pages.
   * Rules are evaluated in order; the first matching rule wins.
   * If no rule matches, uses fallbackUrl instead of internal success page.
   */
  successRedirectRules?: SuccessRedirectRule[]
  /**
   * Fallback external URL when no success redirect rule matches.
   * Can be a single URL string, or an object mapping language codes to URLs.
   * Example: "https://example.com/thank-you" or { it: "https://example.com/it/grazie", en: "https://example.com/en/thank-you" }
   * If not provided, defaults to the internal success page.
   */
  fallbackUrl?: string | Record<string, string>
}

/** Count the number of unique main step groups in a flow */
export function getMainStepCount(flow: FlowConfig): number {
  return new Set(flow.steps.map((s) => s.group)).size
}

/** Get the current main step group number for a given step id */
export function getGroupForStep(flow: FlowConfig, stepId: number): number {
  const step = flow.steps.find((s) => s.id === stepId)
  return step?.group ?? 1
}
